import { useRef, useState } from 'react';
import { getSceneBosConfigFileContent, type SceneContentBaseline, type SceneFieldPatch, type SceneContentRequest, type SceneEditMode } from '@/apis/simulation';
import { changedFiles, contentPayload, editModeAfterChange, fileError, fileFieldPreview, patchFileFields, type FileDraft } from './sceneContentCodec';

export function useSceneContentDraft() {
  const [baseline, setBaseline] = useState<SceneContentBaseline | null>(null);
  const baselineRef = useRef<SceneContentBaseline | null>(null);
  const [files, setFiles] = useState<Record<string, FileDraft>>({});
  const fileRef = useRef<Record<string, FileDraft>>({});
  const generation = useRef(0);
  const latestFields = useRef<SceneFieldPatch>({});
  const [editMode, setEditMode] = useState<SceneEditMode | null>(null);
  const editModeRef = useRef<SceneEditMode | null>(null);
  const setMode = (next: SceneEditMode | null) => { editModeRef.current = next; setEditMode(next); };
  const receipt = useRef<{ signature: string; id: string } | null>(null);

  const replace = (next: Record<string, FileDraft>) => { fileRef.current = next; setFiles(next); };
  const reset = (next: SceneContentBaseline | null = null) => {
    generation.current += 1;
    baselineRef.current = next;
    setBaseline(next);
    replace({});
    receipt.current = null;
    latestFields.current = {};
    setMode(null);
  };

  const load = async (key: string) => {
    const base = baselineRef.current;
    if (!base) throw new Error('请重新进入编辑');
    const row = base.files.find(file => file.key === key);
    if (!row?.editable) throw new Error(row?.reason || '该文件不支持在线编辑');
    if (fileRef.current[row.name]) return;
    const token = generation.current;
    const result = await getSceneBosConfigFileContent(base.sceneId, key, base.baseVersion);
    if (token !== generation.current) return;
    if (!result.editable || !result.sha256 || !result.kind || result.sceneVersion !== base.baseVersion) throw new Error('文件无法无损编辑，请重新加载');
    const file: FileDraft = {
      name: result.name, originalContent: result.content, content: result.content,
      baseSha256: result.sha256, kind: result.kind, error: fileError(result.name, result.content, base.limits),
    };
    // A slower second read may never replace text the user has already typed.
    if (!fileRef.current[row.name]) replace({ ...fileRef.current, [row.name]: patchFileFields(file, latestFields.current, base.limits) });
  };

  const change = (name: string, content: string): SceneFieldPatch | null => {
    const file = fileRef.current[name];
    const base = baselineRef.current;
    if (!file || !base || editModeRef.current === 'fields') return null;
    const error = fileError(name, content, base.limits, file.originalContent);
    const next = { ...fileRef.current, [name]: { ...file, content, error } };
    replace(next);
    setMode(editModeAfterChange(editModeRef.current, 'files', changedFiles(next).length > 0));
    return fileFieldPreview(next, base.fields);
  };

  const syncFields = (fields: SceneFieldPatch, dirty: boolean) => {
    if (editModeRef.current === 'files') return;
    latestFields.current = dirty ? { ...latestFields.current, ...fields } : {};
    setMode(editModeAfterChange(editModeRef.current, 'fields', dirty));
    const base = baselineRef.current;
    if (!base) return;
    replace(Object.fromEntries(Object.entries(fileRef.current).map(([name, file]) => [name, dirty
      ? patchFileFields(file, fields, base.limits)
      : { ...file, content: file.originalContent, error: fileError(name, file.originalContent, base.limits) }])));
  };

  const request = (fields: SceneFieldPatch): SceneContentRequest => {
    const base = baselineRef.current;
    if (!base) throw new Error('请重新进入编辑');
    const payload = contentPayload(base, editModeRef.current, fields, fileRef.current);
    const signature = JSON.stringify(payload);
    if (receipt.current?.signature !== signature) {
      // crypto.randomUUID requires HTTPS; getRandomValues also works on the
      // platform's HTTP development hosts.
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 15) | 64;
      bytes[8] = (bytes[8] & 63) | 128;
      const hex = [...bytes].map(v => v.toString(16).padStart(2, '0')).join('');
      receipt.current = { signature, id: `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` };
    }
    const result = { ...payload, requestId: receipt.current.id };
    if (new TextEncoder().encode(JSON.stringify(result)).length > base.limits.requestBytes) throw new Error('请求内容超过大小限制');
    return result;
  };

  return { baseline, files, editMode, reset, load, change, syncFields, request,
    dirty: editMode !== null,
    filesDirty: editMode === 'files' && changedFiles(files).length > 0,
    error: changedFiles(files).find(file => file.error)?.error ?? null,
  };
}
